import { Fragment, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

import { useTranslation } from '@mint-lib/i18n';
import { typographyVariants } from '@mint-lib/tw-components';
import { ProgressCircular } from '@mint-lib/tw-components/patterns';
import { cn } from '@mint-lib/tw-utils';
import { Chat } from '@mint-sdk/api-ai-assistant';
import { format } from 'date-fns';

import ChatItem from './ChatItem.jsx';
import { ChatListSkeleton } from './ChatListSkeleton.jsx';

type Props = {
  chatId?: string;
  searchValue?: string;
  chatType: 'live' | 'scheduled' | 'all';
  isMobile?: boolean;
  isAgentic?: boolean;
  handleClickItem: (chatId: string) => void;
  onDeleteChat: () => void;
  onChangeChatName?: (newChatName: string) => void;
};

export const ChatList = ({
  chatId,
  searchValue,
  isMobile,
  isAgentic,
  handleClickItem,
  chatType,
  onChangeChatName,
  onDeleteChat,
}: Props) => {
  const { t } = useTranslation('@mint-lib/ai-chat');

  const { data, hasNextPage, fetchNextPage, isFetchingNextPage, isLoading } =
    Chat.getInfiniteChatList.use({
      params: {
        limit: 40,
        chatType: chatType === 'all' ? undefined : chatType,
        searchQuery: searchValue,
      },
    });

  const chats = data?.pages.flat() ?? [];
  const hasChats = chats.length > 0;

  const { ref: sentinelRef, inView } = useInView({
    threshold: 0,
    rootMargin: '0px 0px 200px 0px',
    skip: !hasChats,
  });

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const formatDateLabel = (chatDate: string) => {
    const currentMonth = format(new Date(), 'MMMM');
    const currentYear = format(new Date(), 'yyyy');

    const chatMonth = format(chatDate, 'MMMM');
    const chatYear = format(chatDate, 'yyyy');

    return chatMonth === currentMonth
      ? t('Recents')
      : t('{{val, datetime}}', {
          val: new Date(chatDate + 'Z'),
          formatParams: {
            val: {
              month: 'long',
              ...(currentYear !== chatYear && { year: 'numeric' }),
            },
          },
        });
  };

  if (isLoading) {
    return <ChatListSkeleton />;
  }

  return (
    <div>
      {!hasChats && (
        <p
          className={cn(
            typographyVariants({
              variant: 'bodyLong01',
              color: 'tertiary',
            }),
            'mt-10 w-66 justify-self-center text-center',
          )}
        >
          {t('Start a new conversation with MINT Agentic AI to see it here')}
        </p>
      )}

      <div>
        {chats.map((chat, i, arr) => {
          const isVisibleDateChip =
            i === 0 ||
            format(chat.updatedAt, 'MMMM') !==
              format(arr[i - 1].updatedAt, 'MMMM');

          return (
            <Fragment key={chat.chatSessionId}>
              {isVisibleDateChip && (
                <AgenticDateItem>
                  {formatDateLabel(chat.updatedAt)}
                </AgenticDateItem>
              )}

              <div className="flex justify-between">
                <ChatItem
                  key={chat.chatSessionId}
                  chat={chat}
                  selected={chatId === chat.chatSessionId}
                  onClick={handleClickItem}
                  isMobile={isMobile}
                  isAgentic={isAgentic}
                  onChangeChatName={onChangeChatName}
                  onDeleteChat={onDeleteChat}
                />
              </div>
            </Fragment>
          );
        })}
      </div>
      <div
        className={cn(
          'flex justify-center',
          isFetchingNextPage ? 'flex' : 'hidden',
        )}
      >
        <ProgressCircular size="small" />
      </div>

      <div ref={sentinelRef} aria-hidden />
    </div>
  );
};

const AgenticDateItem = ({ children }: { children: string }) => {
  return (
    <p
      className={cn(
        typographyVariants({
          variant: 'h6',
          color: 'primary',
        }),
        'h-10 pt-4 pb-2 pl-4',
      )}
    >
      {children}
    </p>
  );
};
